import { useState } from "react";
import { Briefcase, Sparkles, History, Gauge, Pencil, Maximize2, CalendarClock, HelpCircle, Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { DetailSection } from "@/components/freelancers/DetailSection";
import { useDisponibilidade, useReconsulta } from "@/hooks/useDisponibilidade";
import { formatDateOnly } from "@/utils/dateUtils";
import {
  getDisponibilidadeConfig,
  axisValueLabel,
  axisValueBadgeClass,
  fonteLabel,
  frescorLabel,
  temperaturaMotivo,
  TEMPERATURA_CONFIG,
} from "@/utils/disponibilidadeUtils";
import { cn } from "@/lib/utils";
import { DisponibilidadeTimeline } from "./DisponibilidadeTimeline";
import { AtualizarDisponibilidadeDialog } from "./AtualizarDisponibilidadeDialog";
import { HistoricoDisponibilidadeDialog } from "./HistoricoDisponibilidadeDialog";

const AXIS_ROWS = [
  {
    dimension: "situacao_emprego",
    label: "Situação de emprego",
    icon: Briefcase,
    hint: "Como a pessoa está trabalhando hoje: empregada com carteira, atuando como freelancer ou entre trabalhos.",
  },
  {
    dimension: "interesse_freela",
    label: "Interesse em freela",
    icon: Sparkles,
    hint: "O quanto a pessoa está a fim de pegar trabalho freela agora.",
  },
  {
    dimension: "relacao_conosco",
    label: "Relação com a gente",
    icon: History,
    hint: "Como está o vínculo da pessoa com a gente: trabalhando agora, já trabalhou antes ou nunca trabalhou.",
  },
] as const;

const OCUPACAO_HINT = "Quantos projetos a pessoa toca em paralelo com a gente no momento.";

interface Props {
  freelancerId: string;
  freelancerNome: string;
}

export function DisponibilidadeCard({ freelancerId, freelancerNome }: Props) {
  const { data: summary } = useDisponibilidade(freelancerId);
  const { data: reconsulta } = useReconsulta(freelancerId);
  const [editOpen, setEditOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const config = getDisponibilidadeConfig(summary?.disponibilidade_efetiva);
  const BadgeIcon = config.icon;
  const frescor = frescorLabel(summary?.frescor_interesse);
  const axes = summary?.axes ?? {};

  return (
    <DetailSection
      title="Disponibilidade"
      icon={CalendarClock}
      action={
        <Button size="sm" variant="outline" onClick={() => setEditOpen(true)}>
          <Pencil className="mr-1.5 h-3.5 w-3.5" />
          Atualizar
        </Button>
      }
    >
      <TooltipProvider delayDuration={100}>
        <div className="grid gap-5 lg:grid-cols-3">
          {/* Coluna maior: situação atual + composição dos eixos */}
          <div className="lg:col-span-2 space-y-4">
            {/* Situação atual — badge do tamanho do texto, não estica */}
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={cn(
                  "inline-flex max-w-full items-center gap-2 rounded-lg border px-3 py-1.5",
                  config.bg,
                  config.border
                )}
              >
                <BadgeIcon className={cn("h-4 w-4 shrink-0", config.color)} />
                <span className={cn("truncate text-sm font-medium", config.color)}>
                  {config.text}
                </span>
              </span>
              {frescor && <span className="text-xs text-muted-foreground">{frescor}</span>}
              {summary?.temperatura && summary.temperatura !== "sem_info" && (() => {
                const tempConfig = TEMPERATURA_CONFIG[summary.temperatura];
                const TempIcon = tempConfig.icon;
                return (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span
                        className={cn(
                          "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium cursor-help",
                          tempConfig.bg,
                          tempConfig.color,
                          tempConfig.border
                        )}
                      >
                        <TempIcon className="h-3.5 w-3.5 shrink-0" />
                        {tempConfig.text}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-[260px]">
                      {temperaturaMotivo(summary)}
                    </TooltipContent>
                  </Tooltip>
                );
              })()}
            </div>

            {/* Datas: de quando é o status e quando alguém falou com a pessoa */}
            <div className="text-xs text-muted-foreground space-y-0.5">
              {summary?.ultimo_sinal && <p>Status desde {formatDateOnly(summary.ultimo_sinal)}</p>}
              {summary && (
                <p>
                  {summary.ultima_consulta
                    ? `Última consulta: ${formatDateOnly(summary.ultima_consulta)}`
                    : "Nunca consultado diretamente"}
                </p>
              )}
              {summary?.reconsulta_vencida && summary.reconsulta_due && (
                <p className="text-amber-600 font-medium">
                  Reconsulta atrasada desde {formatDateOnly(summary.reconsulta_due)}
                </p>
              )}
            </div>

            {/* Composição dos eixos, cada valor com sua cor */}
            <div className="space-y-2">
              {AXIS_ROWS.map(({ dimension, label, icon: RowIcon, hint }) => {
                const axis = axes[dimension];
                const sourceLabel = fonteLabel(axis?.source);
                return (
                  <div key={dimension} className="flex items-center gap-2 text-sm">
                    <RowIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    <span className="flex w-40 shrink-0 items-center gap-1 text-muted-foreground">
                      {label}
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <HelpCircle className="h-3 w-3 shrink-0 cursor-help text-muted-foreground/50 hover:text-muted-foreground" />
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-[240px]">
                          {hint}
                        </TooltipContent>
                      </Tooltip>
                    </span>
                    <span
                      className={cn(
                        "inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium",
                        axisValueBadgeClass(axis?.value)
                      )}
                    >
                      {axisValueLabel(dimension, axis?.value)}
                    </span>
                    {sourceLabel && (
                      <span className="truncate text-xs text-muted-foreground">· {sourceLabel}</span>
                    )}
                  </div>
                );
              })}
              {typeof summary?.carga_atual === "number" && summary.carga_atual > 0 && (
                <div className="flex items-center gap-2 text-sm">
                  <Gauge className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  <span className="flex w-40 shrink-0 items-center gap-1 text-muted-foreground">
                    Ocupação
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="h-3 w-3 shrink-0 cursor-help text-muted-foreground/50 hover:text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-[240px]">
                        {OCUPACAO_HINT}
                      </TooltipContent>
                    </Tooltip>
                  </span>
                  <span
                    className={cn(
                      "inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium",
                      summary.headroom && summary.headroom > 0
                        ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                        : "border-indigo-200 bg-indigo-50 text-indigo-700"
                    )}
                  >
                    {summary.carga_atual} projeto{summary.carga_atual > 1 ? "s" : ""} agora
                    {summary.headroom && summary.headroom > 0 ? " · tem espaço" : ""}
                  </span>
                </div>
              )}
              {reconsulta?.due_at && (
                <div className="flex items-center gap-2 text-sm">
                  <Bell className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  <span className="flex w-40 shrink-0 items-center gap-1 text-muted-foreground">
                    Reconsultar em
                  </span>
                  <span className="inline-flex items-center rounded-md border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
                    {formatDateOnly(reconsulta.due_at)}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Coluna menor: histórico enxuto; abrir completo mora junto da legenda */}
          <div className="lg:col-span-1 lg:border-l lg:pl-5">
            <DisponibilidadeTimeline
              freelancerId={freelancerId}
              compact
              legendAction={
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-6 w-6"
                  onClick={() => setHistoryOpen(true)}
                  title="Ver histórico completo"
                  aria-label="Ver histórico completo"
                >
                  <Maximize2 className="h-3.5 w-3.5" />
                </Button>
              }
            />
          </div>
        </div>
      </TooltipProvider>

      <AtualizarDisponibilidadeDialog
        freelancerId={freelancerId}
        freelancerNome={freelancerNome}
        open={editOpen}
        onOpenChange={setEditOpen}
      />
      <HistoricoDisponibilidadeDialog
        freelancerId={freelancerId}
        freelancerNome={freelancerNome}
        open={historyOpen}
        onOpenChange={setHistoryOpen}
      />
    </DetailSection>
  );
}

export default DisponibilidadeCard;
